import { flowBuilderConstants } from '../../../constants/FlowBuilder';
import { CanvasElement } from '../canvas-element';
import { Orientation } from '../canvas-element/Orientation';
import CanvasText from '../canvas-text/CanvasText';
import { setImageSrc } from '../helpers';
import { FlowButton } from '../TypeDefs';
import { drawBackground, drawHorizontalConnector, drawHorizontalToVerticalConnector, drawIcon, drawVerticalConnector, drawVerticalToHorizontalConnector } from './helpers';

export interface ConnectorLineBtn extends FlowButton {
  // will be calculated in runtime
  left: number;
  top: number;
  right: number;
  bottom: number;
  image: HTMLImageElement;
}

/**
 * Configuration for the connector line.
 * @interface ConnectorLineConfig
 */
export interface ConnectorLineConfig {
  strokeWidth: number; // The width of the stroke.
  strokeColor: string; // The color of the stroke.
  selectedStrokeColor: string; // The color of the stroke when selected.
  fillColor: string; // The fill color of the connector line.
  selectedFillColor: string; // The fill color of the connector line when selected.
  clickRadius: number; // The radius of the click area.
  startConnectorStrokeColor: string; // The color of the stroke for the start connector.
  startBtnWidth: number; // The width of the start button.
  startBtnHeight: number; // The height of the start button.
  startBtnConnectorRadius: number; // The radius of the connector for the start button.
  fontFamily: string; // The font family for the text.
  textFontSize: number; // The font size for the text.
  lineHeightRatio: number; // The line height ratio for the text.
  textColor: string; // The color of the text.
  clickShape: 'circle' | 'diamond'; // The shape of the click area.
  strokeStyle: 'solid' | 'dash' | 'dash-dot' | 'dot', // The style of the Stroke
}

export default class ConnectorLine {
  // variables required for creating instance
  public fromElement?: CanvasElement;

  public isSelected = false;

  private selectable = false;

  public tooltipText = '';

  // other instance variables
  private clickX = 0;

  private clickY = 0;

  private mText = new CanvasText();

  private displayEventVersion = '';

  private textBound = {
    l: 0,
    r: 0,
    t: 0,
    b: 0
  };

  private buttons: ConnectorLineBtn[] = [];

  private hovering = false;

  private btnGroupPosition = {
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    height: 0,
    width: 0
  };

  /**
   * @method create
   * @description create connector
   * @param fromElement from node
   * @param toElement to node
   * @param isSelected is selected node
   * @param selectable is node selectable
   * @param orientation from and to orientation
   * @param text connector text
   * @param tooltipText tooltip text
   * @param buttons buttons on connector
   * @param config connector line Config
   * @returns connector line
   */
  public static create({
    fromElement,
    toElement,
    isSelected = false,
    selectable = true,
    orientation,
    text = '',
    displayEventVersion = '',
    tooltipText,
    buttons = [],
    config,
  }: {
    fromElement?: CanvasElement,
    toElement: CanvasElement,
    isSelected?: boolean,
    selectable?: boolean,
    orientation: { from: Orientation; to: Orientation },
    text?: string,
    displayEventVersion?: string,
    tooltipText?: string,
    buttons?: FlowButton[],
    config: ConnectorLineConfig,
  }) {
    const instance = new ConnectorLine(
      toElement,
      orientation,
      config
    );
    instance.fromElement = fromElement;
    instance.isSelected = isSelected;
    instance.selectable = selectable;
    instance.tooltipText = tooltipText ?? text;
    if (displayEventVersion.length > 0) {
      instance.displayEventVersion = `(${displayEventVersion})`;
    }

    instance.mText.text = text;

    buttons.forEach((b) => {
      const img = new Image();
      setImageSrc(img, b.icon);
      instance.buttons.push({
        ...b,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        image: img
      });
    });

    if (instance.buttons.length > 0) {
      instance.btnGroupPosition.width = instance.buttons.reduce((p, c) => (p + c.width + 12), -12);
      instance.btnGroupPosition.height = instance.buttons[0].height;
    }

    return instance;
  }

  /**
 * @description Private constructor for creating a ConnectorLine instance.
 * @param {CanvasElement} toElement - The target canvas element.
 * @param {{ from: Orientation; to: Orientation }} orientation - The orientation of the connector line.
 * @param {ConnectorLineConfig} config - The configuration for the connector line.
 */
  private constructor(
    public toElement: CanvasElement,
    private orientation: { from: Orientation; to: Orientation },
    private config: ConnectorLineConfig,
  ) {
    // dummy 1 line to avoid blank constructor
    this.toElement = toElement;
  }

  get text() {
    return this.mText.text;
  }

  set text(newName: string) {
    this.mText.text = newName;
  }

  /**
   * @method isMouseOnBody
   * @description Check if mouse is on item body
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns true / false
   */
  isMouseOnBody(mouseX: number, mouseY: number) {
    return this.selectable && (
      Math.abs(mouseX - this.clickX) <= this.config.clickRadius
      && Math.abs(mouseY - this.clickY) <= this.config.clickRadius
    );
  }

  /**
   * @method isMouseOnBtnGroupBody
   * @description Check if mouse is on Button Group
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns true / false
   */
  isMouseOnBtnGroupBody(mouseX: number, mouseY: number): boolean {
    return (
      this.hovering
      && mouseX >= this.btnGroupPosition.left
      && mouseX <= this.btnGroupPosition.right
      && mouseY >= this.btnGroupPosition.top
      && mouseY <= this.btnGroupPosition.bottom
    );
  }

  /**
   * @method performHoverEffect
   * @description perform Hovering Effect
   */
  performHoverEffect() {
    this.hovering = true;
  }

  /**
   * @method removeHoverEffect
   * @description remove Hovering Effect
   */
  removeHoverEffect() {
    this.hovering = false;
  }

  /**
   * @method mouseOnWhichButton
   * @description Find mouse on which Button in Canvas
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns canvasElement Button or null
   */
  mouseOnWhichButton(
    mouseX: number,
    mouseY: number,
  ): ConnectorLineBtn | null {
    if (!this.hovering) {
      return null;
    }
    return this.buttons.find((b) => (
      mouseX >= b.left - 6
      && mouseX <= b.right + 6
      && mouseY >= b.top - 6
      && mouseY <= b.bottom + 6
    )) ?? null;
  }

  /**
   * @method select
   * @description Mark item as selected
   */
  select() {
    this.isSelected = true;
  }

  /**
   * @method deselect
   * @description Mark item as not selected
   */
  deselect() {
    this.isSelected = false;
  }

  /**
   * @method isMouseOnText
   * @description check if mouse is on text
   * @param mouseX mouse x position
   * @param mouseY mouse y position
   * @returns true / false
   */
  isMouseOnText(mouseX: number, mouseY: number) {
    return (
      mouseX >= this.textBound.l
      && mouseX <= this.textBound.r
      && mouseY >= this.textBound.t
      && mouseY <= this.textBound.b
    );
  }

  private drawWrappingTextOnLeft({
    ctx,
    fromX,
    fromY,
    midX,
    midY,
    textLineHeight
  }: {
    ctx: CanvasRenderingContext2D,
    midX: number,
    midY: number,
    fromX: number,
    fromY: number,
    textLineHeight: number
  }) {
    this.mText.wrapText(ctx, midX - fromX - 30, 2, true, this.displayEventVersion);
    const lineCount = this.mText.brokenLines.length;
    this.textBound.l = this.textBound.r - this.mText.width;
    this.textBound.t = midY > fromY ? (midY - textLineHeight) : (midY - lineCount * textLineHeight);
    this.textBound.b = this.textBound.t + textLineHeight * lineCount;
    drawBackground({ ctx, codX: this.textBound.l - 15, codY: this.textBound.t + 2, width: this.textBound.r - this.textBound.l + 25, height: this.textBound.b - this.textBound.t + 10, config: this.config });
    this.mText.brokenLines.forEach((element, i) => {
      ctx.fillText(element, midX - 10, midY > fromY ? (midY + (i * textLineHeight)) : (midY + (i * textLineHeight) - ((this.mText.brokenLines.length - 1) * textLineHeight)));
    });
  }

  /**
   * @method drawTextOnLeft
   * @description Draw text on left of canvas
   * @param ctx canvas context
   * @param fromX from X coordinate
   * @param fromY from Y coordinate
   * @param midX mid X coordinate
   * @param midY mid Y coordinate
   */
  private drawTextOnLeft({
    ctx,
    fromX,
    fromY,
    midX,
    midY
  }: {
    ctx: CanvasRenderingContext2D,
    midX: number,
    midY: number,
    fromX: number,
    fromY: number
  }) {
    const elementBottom = (this.fromElement?.finalBottom ?? 0) + 15;
    const elementTop = (this.fromElement?.top ?? 0) - 15;
    const textLineHeight = this.config.textFontSize * this.config.lineHeightRatio;
    ctx.textAlign = 'right';
    this.textBound.r = midX - 10;
    if (
      (
        (midY > fromY && midY < elementBottom)
        || (fromY > midY && midY > elementTop)
      ) && this.fromElement
    ) {
      this.drawWrappingTextOnLeft({
        ctx,
        fromX,
        fromY,
        midX,
        midY,
        textLineHeight
      });
      return;
    }
    this.mText.wrapText(ctx, 0, 1, true, this.displayEventVersion);
    this.textBound.l = this.textBound.r - this.mText.width;
    this.textBound.t = midY - textLineHeight;
    this.textBound.b = midY + textLineHeight * 0.7;
    drawBackground({ ctx, codX: this.textBound.l - 25, codY: this.textBound.t + 2, width: this.textBound.r - this.textBound.l + 25, height: this.textBound.b - this.textBound.t, config: this.config });
    ctx.fillText(this.mText.brokenLines[0], midX - 25, midY);
  }

  /**
   * @method drawText
   * @description Draw text on canvas
   * @param ctx canvas context
   * @param fromX from X coordinate
   * @param fromY from Y coordinate
   * @param toX to X coordinate
   * @param toY to Y coordinate
   */
  private drawText({
    ctx,
    fromX,
    fromY,
    toX,
    toY
  }: {
    ctx: CanvasRenderingContext2D,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number
  }) {
    if (this.mText.text.length === 0) {
      return;
    }

    const midX = (fromX + toX) / 2;
    const midY = (fromY + toY) / 2;
    // draw the text
    ctx.fillStyle = 'white';
    ctx.font = `400 ${this.config.textFontSize}px ${this.config.fontFamily}`;
    ctx.textBaseline = 'middle';
    const textLineHeight = this.config.textFontSize * this.config.lineHeightRatio;
    if (toX > fromX + 20) {
      if (Math.abs(toY - fromY) > textLineHeight) {
        this.drawTextOnLeft({ ctx, midX, midY, fromX, fromY });
        return;
      }
      ctx.textAlign = 'center';
      this.mText.wrapText(ctx, toX - fromX - 30, 2, true, this.displayEventVersion);
      const lineCount = this.mText.brokenLines.length;
      this.textBound = {
        t: midY - (lineCount + 1) * textLineHeight,
        l: midX - this.mText.width / 2,
        r: midX + this.mText.width / 2,
        b: midY - textLineHeight / 2
      };
      drawBackground({ ctx, codX: this.textBound.l - 15, codY: this.textBound.t + 2, width: this.textBound.r - this.textBound.l + 25, height: this.textBound.b - this.textBound.t, config: this.config });
      this.mText.brokenLines.forEach((element, i) => {
        ctx.fillText(element, midX, midY - ((lineCount - i) * textLineHeight));
      });
      return;
    }
    ctx.textAlign = 'center';
    this.mText.wrapText(ctx, 0, 1, true, this.displayEventVersion);
    this.textBound.l = midX - this.mText.width / 2;
    this.textBound.r = midX + this.mText.width / 2;
    if (toY > fromY) {
      this.textBound.t = midY - textLineHeight * 2;
      this.textBound.b = midY - textLineHeight / 2;
      drawBackground({ ctx, codX: this.textBound.l - 25, codY: this.textBound.t + 3, width: this.textBound.r - this.textBound.l + 25, height: this.textBound.b - this.textBound.t, config: this.config });
      ctx.fillText(this.mText.brokenLines[0], midX - 15, midY - textLineHeight);
      return;
    }
    this.textBound.b = midY + textLineHeight * 2;
    this.textBound.t = midY + textLineHeight / 2;
    drawBackground({ ctx, codX: this.textBound.l - 25, codY: this.textBound.t + 5, width: this.textBound.r - this.textBound.l + 25, height: this.textBound.b - this.textBound.t, config: this.config });
    ctx.fillText(this.mText.brokenLines[0], midX - 15, midY + 10 + textLineHeight);
  }

  /**
   * @method drawLine
   * @description Draw line on canvas
   * @param ctx canvas context
   * @param fromX from X coordinate
   * @param fromY from Y coordinate
   * @param toX to X coordinate
   * @param toY to Y coordinate
   * @param inHX from incoming horizontal X coordinate
   * @param inVY from incoming vertical Y coordinate
   * @param outHX from outgoing horizontal X coordinate
   * @param outVY from outgoing vertical Y coordinate
   */
  private drawLine({
    ctx,
    fromX,
    fromY,
    toX,
    toY,
    inHX,
    inVY,
    outHX,
    outVY
  }: {
    ctx: CanvasRenderingContext2D,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    inHX: number,
    inVY: number,
    outHX: number,
    outVY: number
  }) {
    if (
      // if both from and to orientation are HORIZONTAL
      this.orientation.from === Orientation.HORIZONTAL
      && this.orientation.to === Orientation.HORIZONTAL
    ) {
      drawHorizontalConnector({ ctx, fromX, fromY, toX, toY });
    } else if (
      this.orientation.from === Orientation.VERTICAL
      && this.orientation.to === Orientation.VERTICAL
    ) {
      drawVerticalConnector({ ctx, fromX, fromY, toX, toY });
    } else if (
      this.orientation.from === Orientation.VERTICAL
      && this.orientation.to === Orientation.HORIZONTAL
    ) {
      const { clickX, clickY } = drawVerticalToHorizontalConnector({ ctx, fromX, fromY, toX, toY, outHX, inVY, clickX: this.clickX, clickY: this.clickY });
      this.clickX = clickX;
      this.clickY = clickY;
    } else if (
      this.orientation.from === Orientation.HORIZONTAL
      && this.orientation.to === Orientation.VERTICAL
    ) {
      const { clickX, clickY } = drawHorizontalToVerticalConnector({ ctx, fromX, fromY, toX, toY, outVY, inHX, clickX: this.clickX, clickY: this.clickY });
      this.clickX = clickX;
      this.clickY = clickY;
    }

    ctx.stroke();
    // draw the arrow head
    ctx.setLineDash([]);
    ctx.fillStyle = this.isSelected
      ? this.config.selectedStrokeColor
      : this.config.strokeColor;
    ctx.beginPath();
    if (this.orientation.to === Orientation.HORIZONTAL) {
      ctx.moveTo(toX, toY);
      ctx.lineTo(toX - this.config.clickRadius * 2, toY - this.config.clickRadius * 1.2);
      ctx.lineTo(toX - this.config.clickRadius * 2, toY + this.config.clickRadius * 1.2);
      ctx.lineTo(toX, toY);
      ctx.lineTo(toX - this.config.clickRadius * 2, toY - this.config.clickRadius * 1.2);
    } else {
      ctx.moveTo(toX, toY);
      ctx.lineTo(toX - this.config.clickRadius * 1.2, toY - this.config.clickRadius * 2);
      ctx.lineTo(toX + this.config.clickRadius * 1.2, toY - this.config.clickRadius * 2);
      ctx.lineTo(toX, toY);
      ctx.lineTo(toX - this.config.clickRadius * 2, toY - this.config.clickRadius * 1.2);
    }
    ctx.fill();

    // draw click circle
    ctx.fillStyle = this.isSelected
      ? this.config.selectedFillColor
      : this.config.fillColor;
    ctx.lineWidth = this.config.strokeWidth;
    ctx.beginPath();
    if (this.config.clickShape === 'circle') {
      ctx.arc(this.clickX, this.clickY, this.config.clickRadius, 0, 2 * Math.PI);
    } else {
      ctx.moveTo(this.clickX - this.config.clickRadius * 1.8, this.clickY);
      ctx.lineTo(this.clickX, this.clickY - this.config.clickRadius * 1.8);
      ctx.lineTo(this.clickX + this.config.clickRadius * 1.8, this.clickY);
      ctx.lineTo(this.clickX, this.clickY + this.config.clickRadius * 1.8);
    }
    ctx.fill();
  }

  /**
   * @method calculateToAndFrom
   * @description calculate To and From coordinates
   * @returns incoming, outgoing, to and from horizontal and vertical coordinates
   */
  private calculateToAndFrom() {
    // when fromElement is undefined its coming from start btn
    const startBtnConnector = {
      x: flowBuilderConstants.startBtnPosition.x + this.config.startBtnWidth,
      y: flowBuilderConstants.startBtnPosition.y + this.config.startBtnHeight / 2,
    };
    const outHX = (this.fromElement?.outgoingHX ?? startBtnConnector.x) + (this.fromElement?.config.outConnectorRadius ?? this.config.startBtnConnectorRadius);
    const outHY = this.fromElement?.outgoingHY ?? startBtnConnector.y;
    const outVX = this.fromElement?.outgoingVX ?? 0;
    const outVY = (this.fromElement?.outgoingVY ?? 0) + (this.fromElement?.config.outConnectorRadius ?? this.config.startBtnConnectorRadius);
    const inHX = this.toElement.incomingHX - 2;
    const inHY = this.toElement.incomingHY;
    const inVX = this.toElement.incomingVX;
    const inVY = this.toElement.incomingVY - 2;

    const fromX = this.orientation.from === Orientation.HORIZONTAL ? outHX : outVX;
    const fromY = this.orientation.from === Orientation.HORIZONTAL ? outHY : outVY;
    let toX: number;
    if (this.toElement.hasConvergence()) {
      toX = this.toElement.conditionBoxPosition.left;
    } else {
      toX = this.orientation.to === Orientation.HORIZONTAL ? inHX : inVX;
    }
    const toY = this.orientation.to === Orientation.HORIZONTAL ? inHY : inVY;
    return { inHX, outHX, inVY, outVY, toX, toY, fromX, fromY };
  }

  /**
   * @method draw
   * @description Draw line on canvas
   * @param ctx canvas 2d context
   */
  draw(ctx: CanvasRenderingContext2D) {
    const { fromX, fromY, inHX, inVY, outHX, outVY, toX, toY } = this.calculateToAndFrom();

    ctx.lineWidth = this.config.strokeWidth;
    if (this.mText.text.length > 0) {
      if (this.config.strokeStyle === 'solid') {
        ctx.setLineDash([]);
      } else if (this.config.strokeStyle === 'dash') {
        ctx.setLineDash([4, 2]);
      } else if (this.config.strokeStyle === 'dot') {
        ctx.setLineDash([2, 2]);
      } else if (this.config.strokeStyle === 'dash-dot') {
        ctx.setLineDash([4, 2, 2, 2]);
      }
      ctx.strokeStyle = this.isSelected
        ? this.config.selectedStrokeColor
        : this.config.strokeColor;
    } else {
      // from start btn
      ctx.setLineDash([4, 2]);
      ctx.strokeStyle = this.config.startConnectorStrokeColor;
    }

    ctx.beginPath();

    const midX = (fromX + toX) / 2;
    const midY = (fromY + toY) / 2;

    this.clickX = midX;
    this.clickY = midY;

    ctx.moveTo(fromX, fromY);

    // draw body
    this.drawLine({ ctx, fromX, fromY, toX, toY, inHX, inVY, outHX, outVY });

    // draw the text
    this.drawText({ ctx, fromX, fromY, toX, toY });

    // draw the icon
    drawIcon({ ctx, buttons: this.buttons, btnGroupPosition: this.btnGroupPosition, textBound: this.textBound, hovering: this.hovering });
  }
}
